import api from './api';

export interface RiskClipSegment {
  filename: string;
  url: string;
  mtime: number;
  bytes: number;
}

export interface RiskClipManifest {
  pool_id: number;
  cctv_id: number;
  target_seconds?: number;
  pre_seconds?: number;
  post_seconds?: number;
  segment_duration_sec?: number;
  segment_count: number;
  segments: RiskClipSegment[];
  playlist_url?: string;
  mp4_filename?: string;
  playback_type?: string;
  synthetic?: boolean;
  duration_sec?: number;
  note?: string;
  status?: 'ready' | 'empty' | string;
  trigger_ts?: number;
  archived_at?: string;
  storage_path?: string;
  /** true면 증거 파일이 이미 180 회전됨 — UI 추가 회전 금지 */
  rotate_applied?: boolean;
}
export type RiskEvidenceManifest = RiskClipManifest;

const DROWNING_TYPES = new Set([
  'drowning_risk',
  'drowning',
  'drowning_risk_motion',
  'motion_risk',
  'no_motion',
  'sinking',
]);

/** 시설 시나리오·수동 이벤트 — 대시보드 pre/pro 증거 플레이어 표시 */
const FACILITY_RISK_EVIDENCE_TYPES = new Set([
  'manual',
  'safety_alert',
  'slip_fall',
  'fallen_immobile',
  'collision_person',
  'collision_vehicle',
  'loitering',
  'special_zone_dwell',
  'intrusion',
  'crowd_density',
  'crowd_stampede',
  'near_miss',
  'fall_height',
  'fire_smoke',
  'ppe_violation',
]);

export function isDrowningEventType(eventType?: string): boolean {
  if (!eventType) return false;
  const t = eventType.toLowerCase();
  if (DROWNING_TYPES.has(t) || FACILITY_RISK_EVIDENCE_TYPES.has(t)) return true;
  if (t.includes('drowning')) return true;
  return /(risk|fall|collision|crowd|slip|motion|intrusion)/.test(t);
}

export function resolveEvidenceEventType(event: {
  title?: string;
  metadata?: Record<string, unknown>;
}): string {
  const m = event.metadata;
  if (m) {
    const et = m.event_type != null ? String(m.event_type).trim() : '';
    if (et) return et;
    const rt = m.risk_type != null ? String(m.risk_type).trim() : '';
    if (rt) return rt;
  }
  return String(event.title || '').trim();
}

/**
 * HLS·MP4 재생 URL을 현재 페이지 origin 기준 상대 경로로 맞춤.
 * (dev 3500 + /api 프록시, 307 시 Authorization 유지)
 */
export function normalizePlaybackUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith('/api/')) return url;
  if (typeof window === 'undefined') return url;
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.pathname.startsWith('/api/')) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    /* ignore */
  }
  return url;
}

export function getRiskEvidencePlaylistUrl(eventId: number | string): string {
  return `/api/events/${eventId}/risk-evidence.m3u8`;
}

export function getRiskEvidenceMp4Url(eventId: number | string): string {
  return `/api/events/${eventId}/risk-evidence.mp4`;
}

export function getRiskEvidenceDownloadUrl(eventId: number | string): string {
  return `/api/events/${eventId}/risk-evidence-download`;
}

/** P2-07: 게이트웨이 HLS 최근 N초 세그먼트 매니페스트 */
export async function fetchRiskClipManifest(
  poolId: number,
  cctvId: number,
  seconds = 10,
): Promise<RiskClipManifest> {
  return api.get<RiskClipManifest>(
    `/api/gateway/hls/${poolId}/${cctvId}/risk-clip-manifest`,
    { seconds },
  );
}

/** 이벤트 증거(저장본 우선) 또는 롤링 HLS tail */
export async function fetchEventRiskClip(
  eventId: number | string,
  params?: { cctv_id?: number; seconds?: number; pre_seconds?: number; post_seconds?: number },
): Promise<RiskClipManifest> {
  return api.get<RiskClipManifest>(`/api/events/${eventId}/risk-clip`, params, {
    silentError: true,
  });
}
